import { Affix, Button, Card, Stack, TableOfContents } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ComponentProps, useLayoutEffect, useRef } from "react";

import { cn } from "@/utils/cn";

import { useExpeditionTabContextV2 } from "./ExpeditionTabV2.hooks";

type ExpeditionTabSidebarProps = object;

type ScrollIntoViewOptionsType = {
  behavior: "auto" | "smooth";
  block: "start" | "center" | "end" | "nearest";
  inline: "start" | "center" | "end" | "nearest";
};

export function ExpeditionTabSidebar(_props: ExpeditionTabSidebarProps) {
  const { search, queues } = useExpeditionTabContextV2();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const positionMatcher = new Map<boolean, { affix: ComponentProps<typeof Affix>; card?: ComponentProps<typeof Card> }>([
    [
      true,
      {
        affix: {
          position: { bottom: 0, left: 0, right: 0 }
        },
        card: {
          withBorder: false
        }
      }
    ],
    [
      false,
      {
        affix: { position: { bottom: "50%", right: "md" }, translate: "yes" },
        card: {}
      }
    ]
  ]);

  const scrollIntoViewOptions: ScrollIntoViewOptionsType = {
    behavior: "smooth",
    inline: "center",
    block: "start"
  };

  function applyScrollMarginBasedOnDirection(element: HTMLElement, options: { downMargin?: string; upMargin?: string } = {}) {
    const { downMargin = "72px", upMargin = "136px" } = options;

    // Detecta a direção do scroll baseado na posição atual
    const elementRect = element.getBoundingClientRect();
    const currentScrollY = window.scrollY;
    const elementOffsetTop = elementRect.top + currentScrollY;

    // Se o elemento está abaixo da posição atual (scroll para baixo), usa downMargin
    // Se o elemento está acima da posição atual (scroll para cima), usa upMargin
    const scrollMarginTop = elementOffsetTop > currentScrollY ? downMargin : upMargin;

    element.style.scrollMarginTop = scrollMarginTop;
  }

  function scrollToTop() {
    const element = document.getElementById("top");

    if (!element) return window.scrollTo({ top: 0, behavior: "smooth" });

    applyScrollMarginBasedOnDirection(element, { downMargin: "80px", upMargin: "140px" });
    element.scrollIntoView(scrollIntoViewOptions);
  }

  function scrollToElement(element: HTMLElement) {
    applyScrollMarginBasedOnDirection(element);
    element.scrollIntoView(scrollIntoViewOptions);
  }

  function _scrollToTable(tableIndex: number) {
    const table = document.getElementById(`table${tableIndex}`);

    if (!table) return console.warn(`Elemento com ID 'table${tableIndex}' não encontrado`);

    scrollToElement(table);
  }

  const reinitializeRef = useRef(() => {});
  useLayoutEffect(() => {
    reinitializeRef.current();
  }, [search?.value, queues]);

  return (
    <Affix {...positionMatcher.get(isMobile)?.affix}>
      <Card withBorder {...positionMatcher.get(isMobile)?.card}>
        <Stack gap={4}>
          <Button onClick={scrollToTop} size="sm" color="blue" disabled={false}>
            Top
          </Button>

          <TableOfContents
            variant="light"
            color="blue"
            size="lg"
            radius="sm"
            minDepthToOffset={5}
            classNames={{ root: cn({ "flex-row! flex-wrap gap-[inherit] md:flex-col!": true }), control: cn("max-md:flex-[1_1_100px]!") }}
            reinitializeRef={reinitializeRef}
            scrollSpyOptions={{
              selector: "#root [id*=table-head]",
              getDepth: () => 1,
              getValue: (element) => element.getAttribute("data-value") || ""
            }}
            getControlProps={({ active, data }) => ({
              component: Button,
              disabled: false,
              color: active ? "" : "gray.4",
              size: "lg",
              onClick: () => {
                const element = data.getNode();
                scrollToElement(element);
              },
              children: data.value
            })}
          />
        </Stack>
      </Card>
    </Affix>
  );
}
